import React, { useContext, useEffect, useState, useRef } from "react";
import Ctx from "../../contexts/AppContext";
import { api } from "../../api";
import { crd, btnP, btnSec, sp, rad, type } from "../../constants/styles";
import Ic from "../common/Ic";
import { uploadPhoto } from "../../utils/supabaseClient";
import { getViewableLeaseForTenant } from "../../utils/leaseHelpers";
import SignaturePad from "../landlord/SignaturePad";
import { openSettlementPdf } from "../../utils/settlementPdf";
import { groupMoveInByCategory, leaseMoveInPhotos } from "../../utils/vacatePhotos";

// Category display data — server is source of truth for which categories
// are required (config.required_photo_categories), this map just labels
// them for the UI. Keep in sync with REQUIRED_PHOTO_CATEGORIES in
// server/routes/vacate.js.
const CATEGORY_META = {
  hall:     { label: "Hall / Living Room",  icon: "🛋️" },
  bedroom:  { label: "Bedroom",             icon: "🛏️" },
  kitchen:  { label: "Kitchen",             icon: "🍳" },
  bathroom: { label: "Bathroom",            icon: "🚿" },
  walls:    { label: "Walls",               icon: "🧱" },
  meter:    { label: "Meter Reading",       icon: "📊" },
  other:    { label: "Other",               icon: "📷" },
};

// Per-category photo uploader. Lifted to MODULE SCOPE so the component
// type is stable across VacatePage re-renders. Previously it was defined
// inside VacatePage, which meant every parent state update created a
// NEW component function — React saw a different type each render and
// unmounted+remounted everything inside, including the hidden file
// <input> refs. Symptom: clicking 'Take' or 'Gallery' would sometimes
// open the picker but the onChange never fired because the user was
// interacting with a now-detached input. Moving the component out
// keeps the input refs alive across upload-in-progress state changes.
function CategoryCard({ category, canEdit, vr, photos, setPhotos, flash, config, h, moveInPhotos }) {
  const meta = CATEGORY_META[category] || { label: category, icon: "📷" };
  const myPhotos = photos.filter(p => p.category === category && p.uploaded_by_role === 'tenant');
  // Move-in reference: photos from the lease's "Property Condition
  // Photos" matched to THIS category. Shown as a small strip above
  // the upload buttons so the tenant knows what the room looked
  // like at move-in and can frame their move-out shot similarly —
  // crucial for the landlord's downstream side-by-side comparison
  // to actually be comparable.
  const refPhotos = Array.isArray(moveInPhotos) ? moveInPhotos : [];
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    // Always clear the input value at the END so the same file can be
    // re-picked. Even on early returns — otherwise the picker is a
    // dead button until the user picks a different file.
    const clearInputs = () => {
      try {
        if (cameraRef.current) cameraRef.current.value = "";
        if (galleryRef.current) galleryRef.current.value = "";
      } catch (_) { /* ignore */ }
    };

    if (!files || !files.length) { clearInputs(); return; }
    if (!vr) {
      flash?.("No active vacate request — refresh and try again.", "error");
      clearInputs();
      return;
    }

    setUploading(true);
    try {
      const url = await uploadPhoto(files[0], `vacate_${vr.id}`, { maxWidth: 1024, quality: 0.75 });
      const res = await api.vacateAddPhoto(vr.id, { category, url });
      if (res?.success) {
        setPhotos(prev => [...prev, res.photo]);
      } else {
        flash(res?.error || "Upload failed", "error");
      }
    } catch (e) {
      flash(e?.message || "Upload failed", "error");
    } finally {
      setUploading(false);
      clearInputs();
    }
  };

  const removePhoto = async (pid) => {
    const res = await api.vacateDeletePhoto(vr.id, pid);
    if (res?.success) {
      setPhotos(prev => prev.filter(p => p.id !== pid));
    } else {
      flash(res?.error || "Could not remove", "error");
    }
  };

  const isRequired = config.required_photo_categories.includes(category);
  const done = myPhotos.length > 0;

  return (
    <div style={{
      ...crd(h),
      padding: 12,
      marginBottom: sp.sm,
      borderLeft: `3px solid ${done ? h.gn : (isRequired ? h.wn : h.bd)}`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 18 }}>{meta.icon}</span>
          <span style={{ ...type.sm, color: h.tx, fontWeight: 700 }}>{meta.label}</span>
          {isRequired && <span style={{
            fontSize: 9, fontWeight: 700,
            color: done ? h.gn : h.wn,
            background: done ? `${h.gn}15` : `${h.wn}15`,
            padding: "2px 7px", borderRadius: 999,
          }}>{done ? "✓ DONE" : "REQUIRED"}</span>}
        </div>
        <span style={{ color: h.tt, fontSize: 10 }}>{myPhotos.length} photo{myPhotos.length === 1 ? '' : 's'}</span>
      </div>

      {/* Thumbnails */}
      {myPhotos.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: canEdit ? 8 : 0 }}>
          {myPhotos.map(p => (
            <div key={p.id} style={{ position: "relative", aspectRatio: "1 / 1", borderRadius: rad.sm, overflow: "hidden", border: `1px solid ${h.bd}` }}>
              <img src={p.url} alt={category} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              {canEdit && (
                <button onClick={() => removePhoto(p.id)} title="Remove"
                  style={{
                    position: "absolute", top: 2, right: 2,
                    width: 20, height: 20, borderRadius: 10,
                    background: "rgba(0,0,0,0.65)", color: "#fff",
                    border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
                  }}>×</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Move-in reference strip — shown only when (a) the lease has
          condition photos that map to THIS category, and (b) the
          tenant is still allowed to upload (locked=false). Acts as a
          visual anchor: tenant can see what the room looked like at
          move-in and frame their move-out shot from a similar angle
          so the landlord's side-by-side comparison actually compares
          like-for-like.

          Each thumbnail is tap-to-enlarge (anchor + target=_blank so
          the OS image viewer takes over — no in-app modal needed for
          this read-only reference). Caption surfaces as a hover/tap
          tooltip in case multiple photos exist for the same room. */}
      {refPhotos.length > 0 && (
        <div style={{
          marginBottom: 8,
          paddingBottom: 8,
          borderBottom: `1px dashed ${h.bd}`,
        }}>
          <div style={{
            fontSize: 9, color: h.ts, fontWeight: 600,
            textTransform: "uppercase", letterSpacing: 0.4,
            marginBottom: 4,
            display: "flex", alignItems: "center", gap: 4,
          }}>
            <span>{"📋"}</span>
            <span>MOVE-IN REFERENCE — match this angle</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4 }}>
            {refPhotos.slice(0, 4).map((rp, i) => (
              <a key={i} href={rp.url} target="_blank" rel="noopener noreferrer"
                title={rp.caption || ''}
                style={{
                  display: "block",
                  aspectRatio: "1 / 1",
                  borderRadius: 6,
                  overflow: "hidden",
                  border: `1px solid ${h.bd}`,
                  position: "relative",
                }}>
                <img src={rp.url} alt={rp.caption || ''} loading="lazy"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                {rp.notes && (
                  <div title={rp.notes} style={{
                    position: "absolute", top: 2, right: 2,
                    width: 14, height: 14, borderRadius: 7,
                    background: "rgba(0,0,0,0.55)", color: "#fff",
                    fontSize: 9, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>{"i"}</div>
                )}
              </a>
            ))}
          </div>
          {refPhotos.length > 4 && (
            <div style={{ fontSize: 9, color: h.tt, marginTop: 4 }}>
              +{refPhotos.length - 4} more move-in photo{refPhotos.length - 4 === 1 ? '' : 's'} from the agreement
            </div>
          )}
        </div>
      )}

      {/* Upload buttons */}
      {canEdit && (
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => cameraRef.current?.click()} disabled={uploading} style={{
            flex: 1, padding: "8px 10px", borderRadius: rad.sm, border: "none",
            background: h.ac, color: "#fff", fontSize: 11, fontWeight: 700,
            cursor: uploading ? "default" : "pointer",
          }}>{uploading ? "Uploading..." : "📷 Take"}</button>
          <button onClick={() => galleryRef.current?.click()} disabled={uploading} style={{
            flex: 1, padding: "8px 10px", borderRadius: rad.sm,
            border: `1.5px dashed ${h.bd}`, background: "transparent",
            color: h.ac, fontSize: 11, fontWeight: 600,
            cursor: uploading ? "default" : "pointer",
          }}>🖼 Gallery</button>
          <input ref={cameraRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" hidden
            onChange={e => handleFiles(e.target.files)} />
          <input ref={galleryRef} type="file" accept="image/jpeg,image/png,image/webp" hidden
            onChange={e => handleFiles(e.target.files)} />
        </div>
      )}
    </div>
  );
}

export default function VacatePage() {
  const { h, t, currUserId, leases, go, flash, showConfirm } = useContext(Ctx);

  const [vr, setVr] = useState(null);              // current vacate_request row
  const [photos, setPhotos] = useState([]);         // vacate_photos list
  const [deductions, setDeductions] = useState([]); // Phase 4: shown in proposal review
  const [settlement, setSettlement] = useState(null);// Phase 4: shown post-settle
  const [config, setConfig] = useState({ required_photo_categories: ['hall','bedroom','kitchen','bathroom','walls','meter'] });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [showSigPad, setShowSigPad] = useState(false);   // for accept
  const [disputing, setDisputing] = useState(false);     // showing dispute reason form
  const [disputeReason, setDisputeReason] = useState("");

  const lease = getViewableLeaseForTenant(leases, currUserId);
  // Pre-group the lease's "Property Condition Photos" by resolved
  // vacate category. Drives the move-in reference strip inside each
  // CategoryCard so the tenant has a visual anchor while shooting
  // move-out photos. See utils/vacatePhotos.js for the caption→enum
  // mapping (Kitchen, Bed Room, Home Theater, Toilet, etc.).
  const moveInByCat = groupMoveInByCategory(leaseMoveInPhotos(lease));

  // ── Initial load: find this tenant's most recent vacate request.
  // Prefer an open one; otherwise show the most recent settled/
  // cancelled one so the tenant can see their settlement receipt.
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.vacateListMine();
        if (res?.success) {
          const sorted = (res.vacate_requests || []).slice().sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
          const open = sorted.find(v => v.status !== 'settled' && v.status !== 'cancelled');
          const latest = open || sorted[0];
          if (latest) {
            const full = await api.vacateGet(latest.id);
            if (full?.success) {
              setVr(full.vacate_request);
              setPhotos(full.photos || []);
              setDeductions((full.deductions || []).map(d => ({
                ...d,
                proof_photo_ids: (() => { try { return JSON.parse(d.proof_photo_ids || '[]'); } catch { return []; } })(),
              })));
              setSettlement(full.settlement || null);
              setConfig(full.config || config);
              setNotesDraft(full.vacate_request.tenant_notes || "");
            }
          }
        }
      } catch (e) {
        console.error('vacate load:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    if (!vr) return;
    const res = await api.vacateGet(vr.id);
    if (res?.success) {
      setVr(res.vacate_request);
      setPhotos(res.photos || []);
      setDeductions((res.deductions || []).map(d => ({
        ...d,
        proof_photo_ids: (() => { try { return JSON.parse(d.proof_photo_ids || '[]'); } catch { return []; } })(),
      })));
      setSettlement(res.settlement || null);
    }
  };

  // ── Actions ───────────────────────────────────────────────────────
  const initiate = async () => {
    if (!lease?.id) {
      flash("No active lease found", "error");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.vacateInitiate({ lease_id: lease.id });
      if (res?.success) {
        flash("Vacate request started — upload move-out photos below");
        setVr(res.vacate_request);
        setPhotos([]);
      } else {
        flash(res?.error || res?.message || "Could not start vacate", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const saveNotes = async () => {
    if (!vr) return;
    const res = await api.vacateUpdateNotes(vr.id, { tenant_notes: notesDraft });
    if (res?.success) {
      setVr(res.vacate_request);
      flash("Notes saved");
    } else {
      flash(res?.error || "Could not save notes", "error");
    }
  };

  const cancel = async () => {
    if (!vr) return;
    showConfirm("Cancel this vacate request? You can start a new one anytime.", async () => {
      const res = await api.vacateTransition(vr.id, 'cancel');
      if (res?.success) {
        flash("Vacate request cancelled");
        setVr(null);
        setPhotos([]);
        setDeductions([]);
      } else {
        flash(res?.error || "Could not cancel", "error");
      }
    });
  };

  // ── Phase 4: review landlord's proposal ───────────────────────
  const acceptWithSignature = async (signatureDataUrl) => {
    if (!vr) return;
    setSubmitting(true);
    try {
      const res = await api.vacateTransition(vr.id, 'accept', { signature: signatureDataUrl });
      if (res?.success) {
        setShowSigPad(false);
        flash("Accepted — awaiting landlord to finalise the settlement");
        await refresh();
      } else {
        flash(res?.message || res?.error || "Could not accept", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const dispute = async () => {
    if (!vr) return;
    if (!disputeReason.trim()) { flash("Please describe why you dispute the proposal.", "error"); return; }
    setSubmitting(true);
    try {
      const res = await api.vacateTransition(vr.id, 'dispute', { reason: disputeReason.trim() });
      if (res?.success) {
        setDisputing(false);
        flash("Dispute raised — an admin will review");
        await refresh();
      } else {
        flash(res?.message || res?.error || "Could not raise dispute", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitLock = async () => {
    if (!vr) return;
    setSubmitting(true);
    try {
      const res = await api.vacateTransition(vr.id, 'submit');
      if (res?.success) {
        flash("Submitted — landlord will review your move-out photos");
        await refresh();
      } else if (res?.error === 'missing_required_photos') {
        const labels = (res.missing || []).map(c => CATEGORY_META[c]?.label || c).join(', ');
        flash(`Add at least one photo for: ${labels}`, "error");
      } else {
        flash(res?.message || res?.error || "Could not submit", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────
  if (loading) {
    return <div style={{ paddingTop: 40, textAlign: "center", color: h.ts }}>Loading vacate status…</div>;
  }

  // Helper: format a date for the cancelled-banner. Falls back gracefully.
  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return ''; }
  };

  // No active request OR previous request was cancelled — show the
  // initiate screen so the tenant can start fresh. Cancelled records
  // have no useful detail (no settlement, no deductions); leaving the
  // tenant stranded on the locked view with no upload buttons and no
  // visible reason was the cause of "the buttons disappeared" reports.
  // Settled is intentionally NOT routed here — settled requests carry
  // the deposit-settlement receipt the tenant may want to revisit.
  const isCancelled = vr?.status === 'cancelled';
  if (!vr || isCancelled) {
    return (
      <div style={{ paddingTop: 16 }}>
        <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>← Back</button>
        <h2 style={{ ...type.h1, color: h.tx, margin: "0 0 12px" }}>Vacate Property</h2>

        {isCancelled && (
          <div style={{
            ...crd(h),
            padding: 14,
            marginBottom: 12,
            background: `${h.wn}08`,
            borderLeft: `4px solid ${h.wn}`,
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <span style={{ fontSize: 20 }}>{"ℹ️"}</span>
              <div style={{ flex: 1 }}>
                <div style={{ ...type.body, color: h.tx, fontWeight: 700, marginBottom: 2 }}>
                  Previous request was cancelled
                </div>
                <div style={{ color: h.ts, fontSize: 12, lineHeight: 1.5 }}>
                  Your move-out request from {fmtDate(vr.cancelled_at || vr.created_at)} was cancelled, so it can't be edited.
                  If you still want to move out, start a fresh request below — it'll have its own photo
                  upload, notes, and landlord-review flow.
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ ...crd(h), padding: 16, marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div style={{ fontSize: 32 }}>📦</div>
            <div>
              <div style={{ ...type.body, color: h.tx, fontWeight: 700, marginBottom: 4 }}>
                Moving out?
              </div>
              <div style={{ color: h.ts, fontSize: 12, lineHeight: 1.5 }}>
                Start a vacate request to begin the move-out process. You'll need to:
              </div>
              <ol style={{ color: h.sc, fontSize: 12, lineHeight: 1.7, paddingLeft: 18, margin: "8px 0 0" }}>
                <li>Upload photos of each room and the meter reading</li>
                <li>Add any notes about the property's condition</li>
                <li>Submit & lock your record</li>
                <li>Wait for your landlord to review and propose the deposit refund</li>
              </ol>
            </div>
          </div>
        </div>

        {!lease ? (
          <div style={{ ...crd(h), padding: 16, textAlign: "center", color: h.ts }}>
            No active lease found. Contact your landlord.
          </div>
        ) : (
          <button onClick={initiate} disabled={submitting} style={{ ...btnP(h), width: "100%", padding: 14 }}>
            {submitting ? "Starting..." : isCancelled ? "📦 Start a New Vacate Request" : "📦 Initiate Vacate Request"}
          </button>
        )}
      </div>
    );
  }

  // Active request — show category grid (editable in 'initiated', read-only after)
  const locked = vr.status !== 'initiated';
  // Tenants may add/replace move-out photos through review — until the
  // landlord proposes a settlement (then the record is dispute evidence).
  const canEdit = vr.status === 'initiated' || vr.status === 'under_review';
  const requiredDone = config.required_photo_categories.every(c =>
    photos.some(p => p.category === c && p.uploaded_by_role === 'tenant')
  );

  return (
    <div style={{ paddingTop: 16 }}>
      <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>← Back</button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h2 style={{ ...type.h1, color: h.tx, margin: 0 }}>Vacate Request</h2>
        <span style={{
          padding: "4px 12px", borderRadius: 999, fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
          textTransform: "uppercase",
          background: locked ? `${h.gn}15` : `${h.wn}15`,
          color: locked ? h.gn : h.wn,
        }}>{vr.status.replace(/_/g, ' ')}</span>
      </div>

      {/* Status-aware proposal / settlement panels ── Phase 4 */}
      {vr.status === 'under_review' && (
        <div style={{ ...crd(h), padding: 12, marginBottom: 12, background: `${h.gn}08`, border: `1px solid ${h.gn}25` }}>
          <div style={{ color: h.gn, fontWeight: 700, fontSize: 12, marginBottom: 4 }}>✓ Submitted — awaiting landlord review</div>
          <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>
            You can still add or replace your move-out photos until the landlord proposes. They will review and either approve a
            full refund of your deposit or propose deductions. You'll be notified when there's an update.
          </div>
        </div>
      )}

      {/* Proposal: full refund OR deduction. Both share the summary
          card; deduction view adds the line items. */}
      {(vr.status === 'full_refund_proposed' || vr.status === 'deduction_proposed') && (() => {
        const depositRs = Number(lease?.deposit) || 0;
        const depositPaise = depositRs * 100;
        const totalDed = deductions.reduce((s, d) => s + Number(d.amount || 0), 0);
        const refundPaise = Math.max(0, depositPaise - totalDed);
        const fmt = (paise) => "₹" + Math.round(Number(paise) / 100).toLocaleString('en-IN');
        return (
          <div style={{ ...crd(h), padding: 14, marginBottom: 12, border: `2px solid ${h.ac}30`, background: `${h.ac}05` }}>
            <div style={{ ...type.cap, color: h.ac, marginBottom: 8, fontWeight: 700 }}>
              📩 {vr.status === 'full_refund_proposed' ? 'Full refund proposed' : 'Settlement proposal'}
            </div>

            {/* Landlord notes if present */}
            {vr.landlord_notes && (
              <div style={{ background: h.bg, padding: 8, borderRadius: rad.sm, marginBottom: 10, border: `1px solid ${h.bd}` }}>
                <div style={{ fontSize: 9, color: h.ts, fontWeight: 700, marginBottom: 2, letterSpacing: 0.3 }}>LANDLORD'S NOTES</div>
                <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{vr.landlord_notes}</div>
              </div>
            )}

            {/* Deduction line items */}
            {vr.status === 'deduction_proposed' && deductions.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 9, color: h.ts, fontWeight: 700, marginBottom: 4, letterSpacing: 0.3 }}>DEDUCTIONS ({deductions.length})</div>
                {deductions.map(d => {
                  const proofs = (d.proof_photo_ids || []).map(pid => photos.find(p => p.id === pid)).filter(Boolean);
                  const labels = { damage: 'Property Damage', cleaning: 'Cleaning', painting: 'Painting', unpaid_bills: 'Unpaid Bills', other: 'Other' };
                  return (
                    <div key={d.id} style={{ background: h.bg, padding: 8, borderRadius: rad.sm, marginBottom: 4, border: `1px solid ${h.bd}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: h.tx, fontWeight: 700, fontSize: 12 }}>{labels[d.reason] || d.reason}</div>
                          {d.description && <div style={{ color: h.sc, fontSize: 11, marginTop: 2 }}>{d.description}</div>}
                          {proofs.length > 0 && (
                            <div style={{ display: 'flex', gap: 3, marginTop: 4 }}>
                              {proofs.map(p => (
                                <img key={p.id} src={p.url} alt="proof" style={{ width: 30, height: 30, objectFit: 'cover', borderRadius: 3, border: `1px solid ${h.bd}` }} />
                              ))}
                            </div>
                          )}
                        </div>
                        <div style={{ color: h.rd, fontWeight: 700, fontSize: 12 }}>-{fmt(d.amount)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Refund summary */}
            <div style={{ background: h.ib, padding: 10, borderRadius: rad.sm, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: h.sc, marginBottom: 4 }}>
                <span>Your deposit</span><span>{fmt(depositPaise)}</span>
              </div>
              {totalDed > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: h.rd, marginBottom: 4 }}>
                  <span>Total deductions</span><span>-{fmt(totalDed)}</span>
                </div>
              )}
              <div style={{ height: 1, background: h.bd, margin: '6px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700 }}>
                <span style={{ color: h.tx }}>Refund to you</span>
                <span style={{ color: refundPaise > 0 ? h.gn : h.ts }}>{fmt(refundPaise)}</span>
              </div>
            </div>

            {/* Dispute composer (replaces button when active) */}
            {disputing ? (
              <div>
                <div style={{ ...type.cap, color: h.ts, marginBottom: 6 }}>Why are you disputing?</div>
                <textarea
                  value={disputeReason}
                  onChange={e => setDisputeReason(e.target.value)}
                  placeholder="Explain which deduction you disagree with and why. An admin will review."
                  rows={4}
                  maxLength={1000}
                  style={{
                    width: "100%", padding: 8, borderRadius: rad.sm,
                    border: `1px solid ${h.bd}`, background: h.bg, color: h.tx,
                    fontSize: 12, fontFamily: "inherit", resize: "vertical",
                    boxSizing: "border-box", marginBottom: 6,
                  }}
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => { setDisputing(false); setDisputeReason(""); }}
                    style={{ ...btnSec(h), flex: 1, padding: 10, fontSize: 12 }}>Back</button>
                  <button onClick={dispute} disabled={submitting || !disputeReason.trim()}
                    style={{
                      flex: 2, padding: 10, borderRadius: rad.md, border: "none",
                      background: h.rd, color: "#fff", fontWeight: 700, fontSize: 12,
                      opacity: (submitting || !disputeReason.trim()) ? 0.55 : 1,
                      cursor: (submitting || !disputeReason.trim()) ? "not-allowed" : "pointer",
                    }}>{submitting ? "Submitting..." : "🚩 Raise Dispute"}</button>
                </div>
              </div>
            ) : showSigPad ? (
              <div style={{ background: h.bg, padding: 10, borderRadius: rad.sm, border: `1px solid ${h.bd}` }}>
                <div style={{ ...type.cap, color: h.ts, marginBottom: 6 }}>Sign to accept</div>
                <div style={{ color: h.sc, fontSize: 10, marginBottom: 8, lineHeight: 1.5 }}>
                  By signing below, you accept the settlement terms above. This e-signature is legally
                  binding under the Indian IT Act, 2000.
                </div>
                <SignaturePad h={h}
                  onSave={acceptWithSignature}
                  onCancel={() => setShowSigPad(false)}
                />
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setShowSigPad(true)} disabled={submitting}
                  style={{
                    flex: 2, padding: 12, borderRadius: rad.md, border: "none",
                    background: h.gn, color: "#fff", fontWeight: 700, fontSize: 12,
                    cursor: submitting ? "not-allowed" : "pointer",
                  }}>✅ Accept & Sign</button>
                {vr.status === 'deduction_proposed' && (
                  <button onClick={() => setDisputing(true)} disabled={submitting}
                    style={{
                      flex: 1, padding: 12, borderRadius: rad.md,
                      border: `1px solid ${h.rd}`, background: 'transparent',
                      color: h.rd, fontWeight: 700, fontSize: 12,
                      cursor: submitting ? "not-allowed" : "pointer",
                    }}>🚩 Dispute</button>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Agreed (between accept and settle) */}
      {vr.status === 'agreed' && (
        <div style={{ ...crd(h), padding: 12, marginBottom: 12, background: `${h.gn}08`, border: `1px solid ${h.gn}25` }}>
          <div style={{ color: h.gn, fontWeight: 700, fontSize: 12, marginBottom: 4 }}>
            ✓ {vr.admin_resolution_note ? 'Admin ruling posted' : 'You accepted'} — awaiting landlord to finalise settlement
          </div>
          {vr.admin_resolution_note && (
            <div style={{
              margin: '6px 0', padding: 8,
              background: h.bg, borderRadius: rad.sm, border: `1px solid ${h.bd}`,
            }}>
              <div style={{ fontSize: 9, color: h.ts, fontWeight: 700, letterSpacing: 0.3, marginBottom: 3 }}>⚖️ ADMIN RULING</div>
              <div style={{ color: h.tx, fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{vr.admin_resolution_note}</div>
            </div>
          )}
          <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>
            The landlord will sign and finalise the settlement next. Once they confirm, your deposit refund
            will be processed and the agreement will be closed.
          </div>
        </div>
      )}

      {/* Disputed */}
      {vr.status === 'disputed' && (
        <div style={{ ...crd(h), padding: 12, marginBottom: 12, background: `${h.wn}10`, border: `1px solid ${h.wn}40` }}>
          <div style={{ color: h.wn, fontWeight: 700, fontSize: 12, marginBottom: 4 }}>🚩 Dispute under admin review</div>
          {vr.dispute_reason && (
            <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5, marginBottom: 4, whiteSpace: 'pre-wrap' }}>
              <b>Your reason:</b> {vr.dispute_reason}
            </div>
          )}
          <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>
            An admin will review the proposal + your dispute and post a final ruling. Both you and the
            landlord will be notified when the dispute is resolved.
          </div>
        </div>
      )}

      {/* Settled — show the final receipt */}
      {vr.status === 'settled' && settlement && (() => {
        const fmt = (paise) => "₹" + Math.round(Number(paise) / 100).toLocaleString('en-IN');
        return (
          <div style={{ ...crd(h), padding: 14, marginBottom: 12, background: `${h.gn}08`, border: `2px solid ${h.gn}30` }}>
            <div style={{ ...type.cap, color: h.gn, marginBottom: 8, fontWeight: 700 }}>
              ✓ SETTLED — agreement closed
            </div>
            <div style={{ background: h.bg, padding: 10, borderRadius: rad.sm, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: h.sc, marginBottom: 4 }}>
                <span>Deposit</span><span>{fmt(settlement.deposit_amount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: h.rd, marginBottom: 4 }}>
                <span>Total deductions</span><span>-{fmt(settlement.total_deductions)}</span>
              </div>
              <div style={{ height: 1, background: h.bd, margin: '6px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700 }}>
                <span style={{ color: h.tx }}>Refund</span>
                <span style={{ color: h.gn }}>{fmt(settlement.refund_amount)}</span>
              </div>
            </div>
            <div style={{ fontSize: 10, color: h.ts, lineHeight: 1.5, marginBottom: 8 }}>
              Settled on {new Date(settlement.settled_at).toLocaleDateString('en-IN')} · Resolution: {settlement.resolution.replace(/_/g,' ')}
            </div>
            <button onClick={() => openSettlementPdf({
              settlement, vacate_request: vr, deductions, photos,
              lease, tenant: { name: vr.tenant_name, ph: vr.tenant_phone },
            })} style={{
              width: "100%", padding: 10, borderRadius: rad.md, border: "none",
              background: h.ac, color: "#fff", fontWeight: 700, fontSize: 12,
              cursor: "pointer",
            }}>📄 Download Settlement Receipt</button>
          </div>
        );
      })()}

      {/* Category grid — visible in every state for the tenant's own
          reference. Editable only when status='initiated' (handled by
          CategoryCard's `locked` prop). */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ ...type.cap, color: h.ts, marginBottom: 8 }}>
          Move-out photos {locked ? '' : `· ${photos.filter(p => p.uploaded_by_role === 'tenant').length} uploaded`}
        </div>
        {config.required_photo_categories.map(cat => (
          <CategoryCard key={cat} category={cat} canEdit={canEdit}
            vr={vr} photos={photos} setPhotos={setPhotos}
            flash={flash} config={config} h={h}
            moveInPhotos={moveInByCat[cat] || []} />
        ))}
        {/* Optional 'other' bucket — only when not locked, lets tenants
            include damage they want to point out explicitly. The 'other'
            move-in slot picks up lease photos whose captions didn't
            map to any structured category (e.g. "Home Theater"). */}
        {canEdit && <CategoryCard category="other" canEdit={true}
          vr={vr} photos={photos} setPhotos={setPhotos}
          flash={flash} config={config} h={h}
          moveInPhotos={moveInByCat['other'] || []} />}
      </div>

      {/* Notes */}
      <div style={{ ...crd(h), padding: 12, marginBottom: 12 }}>
        <div style={{ ...type.cap, color: h.ts, marginBottom: 6 }}>Notes for landlord</div>
        {locked ? (
          <div style={{ color: h.sc, fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
            {vr.tenant_notes || <i style={{ color: h.tt }}>No notes added.</i>}
          </div>
        ) : (
          <>
            <textarea
              value={notesDraft}
              onChange={e => setNotesDraft(e.target.value)}
              placeholder="Mention any context — e.g. 'AC was already noisy', 'painted wall myself with landlord permission', etc."
              rows={4}
              maxLength={1000}
              style={{
                width: "100%", padding: 8, borderRadius: rad.sm,
                border: `1px solid ${h.bd}`, background: h.bg, color: h.tx,
                fontSize: 12, fontFamily: "inherit", resize: "vertical",
                boxSizing: "border-box",
              }}
            />
            <button onClick={saveNotes} style={{ ...btnSec(h), marginTop: 6, padding: "6px 12px", fontSize: 11 }}>
              Save Notes
            </button>
          </>
        )}
      </div>

      {/* Submit + Cancel actions — only before submit (initiated) */}
      {!locked && (
        <>
          <button onClick={submitLock} disabled={submitting || !requiredDone}
            style={{
              ...btnP(h), width: "100%", padding: 14,
              opacity: (!requiredDone || submitting) ? 0.5 : 1,
              cursor: (!requiredDone || submitting) ? "not-allowed" : "pointer",
              marginBottom: 8,
            }}>
            {submitting ? "Submitting..." : requiredDone ? "🔒 Submit & Lock Record" : `Upload all ${config.required_photo_categories.length} required categories first`}
          </button>
          <button onClick={cancel} style={{
            width: "100%", padding: 10, background: "transparent",
            border: `1px solid ${h.rd}40`, color: h.rd,
            borderRadius: rad.md, fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}>Cancel Request</button>
        </>
      )}
    </div>
  );
}
